const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ota-backend.altariumconsulting.com";

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("JWTtoken")}`,
});

async function handle(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    const message =
      (data.error && (data.error.message || data.error)) ||
      data.message ||
      "Something went wrong. Please try again.";
    throw new Error(message);
  }
  return data;
}

// --- Identity: consumer /google and /apple both take a raw ID token and
// return { token, user } (google also returns response_code). Their shape
// doesn't follow the { success, data } convention of the rest of the API,
// so callers handle the response directly rather than via handle().
export const googleLogin = (idToken, referralCodeProvided) =>
  fetch(`${BACKEND_URL}/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: idToken, referralCodeProvided }),
  }).then((r) => r.json());

export const appleLogin = (idToken, referralCodeProvided) =>
  fetch(`${BACKEND_URL}/apple`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: idToken, referralCodeProvided }),
  }).then((r) => r.json());

export const logoutShopCustomer = () => {
  localStorage.removeItem("JWTtoken");
  localStorage.removeItem("userId");
};

// --- Onboarding ---
export const updateUserDobNickname = ({ cuid, nickname, dob }) => {
  const params = new URLSearchParams({ cuid });
  if (nickname) params.set("nickname", nickname);
  if (dob) params.set("dob", dob);
  return fetch(`${BACKEND_URL}/auth/user?${params.toString()}`, {
    method: "PUT",
    headers: authHeaders(),
  }).then(handle);
};

export const getConsent = () =>
  fetch(`${BACKEND_URL}/auth/consent`, { headers: authHeaders() }).then(
    handle,
  );

export const updateConsent = (payload) =>
  fetch(`${BACKEND_URL}/auth/consent`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(handle);

// --- Shops & products ---
export const getActiveShops = ({ page = 1, limit = 20, search } = {}) => {
  const params = new URLSearchParams({ page, limit });
  if (search) params.set("search", search);
  return fetch(`${BACKEND_URL}/shopfront/app/shops?${params}`, {
    headers: authHeaders(),
  }).then(handle);
};

export const getShopDetails = (shopId, { page = 1, limit = 50 } = {}) => {
  const params = new URLSearchParams({ page, limit });
  return fetch(`${BACKEND_URL}/shopfront/app/shops/${shopId}?${params}`, {
    headers: authHeaders(),
  }).then(handle);
};

// --- Cart / checkout ---
export const getCheckoutPreview = (shopId, body) =>
  fetch(`${BACKEND_URL}/shopfront/app/shops/${shopId}/checkout-preview`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then(handle);

export const createCheckout = (shopId, body) =>
  fetch(`${BACKEND_URL}/shopfront/app/shops/${shopId}/checkout`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  }).then(handle);

// --- Orders ---
export const getOrder = (orderId) =>
  fetch(`${BACKEND_URL}/shopfront/app/orders/${orderId}`, {
    headers: authHeaders(),
  }).then(handle);

export const getMyOrders = ({
  page = 1,
  limit = 10,
  sortBy = "newest",
} = {}) => {
  const params = new URLSearchParams({ page, limit, sortBy });
  return fetch(`${BACKEND_URL}/shopfront/app/orders?${params}`, {
    headers: authHeaders(),
  }).then(handle);
};

export const cancelOrder = (orderId) =>
  fetch(`${BACKEND_URL}/shopfront/app/orders/${orderId}/cancel`, {
    method: "POST",
    headers: authHeaders(),
  }).then(handle);

// --- Delivery addresses ---
// Note: these endpoints read `userId` from the query string (existing
// backend convention for this controller), not from the bearer token.
export const getAddresses = () => {
  const userId = localStorage.getItem("userId");
  return fetch(`${BACKEND_URL}/projects/users/addresses?userId=${userId}`, {
    headers: authHeaders(),
  }).then(handle);
};

export const addAddress = (payload) => {
  const userId = localStorage.getItem("userId");
  return fetch(`${BACKEND_URL}/projects/users/addresses?userId=${userId}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(payload),
  }).then(handle);
};

export const setDefaultAddress = (addressId) => {
  const userId = localStorage.getItem("userId");
  return fetch(
    `${BACKEND_URL}/projects/addresses/${addressId}/default?userId=${userId}`,
    { method: "PATCH", headers: authHeaders() },
  ).then(handle);
};
